import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Accordion,
  Icon,
  Form,
  Button,
  Message,
  Header,
  Transition,
} from 'semantic-ui-react';
import ExternalAccountStatuses from './ExternalAccountStatuses';
import AvailabilityView from './AvailabilityView';
import AttendanceRequirement from './AttendanceRequirement';
import LeadTime from './LeadTime';
import DateRange from './DateRange';
import BufferTime from './BufferTime';
import { UPDATE_PROJECT, PROJECT_OWNERS_EXTERNAL_ACCOUNT_STATUSES } from '../../../../../queries/project';

const Scheduling = ({ project: originalProject }) => {
  const expanded = originalProject.owners.length > 1;
  const [prevExpanded, setPrevExpanded] = useState(expanded);
  const [open, setOpen] = useState(expanded);
  const [isSaved, setIsSaved] = useState(false);
  const [schedulingSettings, setSchedulingSettings] = useState(originalProject.schedulingSettings);
  const [updateProject, { loading, error }] = useMutation(UPDATE_PROJECT, {
    onCompleted: () => {
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
    },
  });

  if (prevExpanded !== expanded) {
    setOpen(expanded);
    setPrevExpanded(expanded);
  }

  const { data } = useQuery(PROJECT_OWNERS_EXTERNAL_ACCOUNT_STATUSES, {
    variables: { id: originalProject.id },
    fetchPolicy: 'network-only',
  });

  const project = {
    ...originalProject,
    owners: data?.project?.owners || originalProject.owners,
  };

  return (
    <Accordion>
      <Accordion.Title
        active={open}
        onClick={() => setOpen(!open)}
      >
        Additional scheduling settings
        <Icon name="dropdown" />
      </Accordion.Title>
      <Accordion.Content active={open}>
        <Form
          className="accordion-content"
          onSubmit={() => updateProject({
            variables: {
              id: project.id,
              input: {
                schedulingSettings: {
                  attendanceRequirement: schedulingSettings.attendanceRequirement,
                  leadHours: parseInt(schedulingSettings.leadHours, 10),
                  bufferMinutes: parseInt(schedulingSettings.bufferMinutes, 10),
                  availableDays: parseInt(schedulingSettings.availableDays, 10),
                  startDate: schedulingSettings.startDate,
                  endDate: schedulingSettings.endDate,
                },
              },
            },
          })}
        >
          {error && <Message error={error !== null} content={error.message} />}
          <Header.Subheader className="current-availability">
            {'These settings affect when experts can schedule meetings, '}
            <AvailabilityView project={project} />
            <ExternalAccountStatuses project={project} />
          </Header.Subheader>
          <AttendanceRequirement
            project={project}
            settings={schedulingSettings}
            setSettings={setSchedulingSettings}
          />
          <LeadTime settings={schedulingSettings} setSettings={setSchedulingSettings} />
          <DateRange
            project={project}
            settings={schedulingSettings}
            setSettings={setSchedulingSettings}
          />
          <BufferTime settings={schedulingSettings} setSettings={setSchedulingSettings} />
          {isSaved && (
          <Transition visible={isSaved} animation="scale" duration={500}>
            <Header color="green">Successfully saved!</Header>
          </Transition>
          )}
          <Button
            className="primary-btn"
            loading={loading}
            disabled={loading}
          >
            Save
          </Button>
        </Form>
      </Accordion.Content>
    </Accordion>
  );
};

export default Scheduling;
